import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import {
  postPassengersWalletByAdmin,
  getPassengerWalletByAdmin,
} from "../api/passengerWalletApi";

// ═══════════════════════════════════════════════════════
// 🎨 BRAND COLORS — same green used across passenger app
// ═══════════════════════════════════════════════════════
const BRAND = {
  primary: "#419952",
  primaryDark: "#357A43",
  primaryLight: "#5BB56A",
  primaryBg: "#EAF5EC",
  primaryBorder: "#B4D9BB",
};

const PAGE_SIZE = 10; // oru page-ku 10 records

// ✅ Wallet balance list API response-laye varum (per-passenger call illa)
const getListBalance = (p) => {
  const b = p.walletBalance ?? p.balance ?? p.wallet?.balance;
  return b === undefined || b === null || Number.isNaN(Number(b))
    ? null
    : Number(b);
};

export default function PassengerWalletPage() {
  // ── Passenger list (server-side paginated) ───────────────────────────────
  const [passengers, setPassengers] = useState([]);
  const [balanceOverrides, setBalanceOverrides] = useState({}); // drawer-la refresh aana balance
  const [loadingList, setLoadingList] = useState(true);

  // ── Search ────────────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState(""); // input-la type panradhu
  const [activeTerm, setActiveTerm] = useState(""); // Search click panna term

  // ── Pagination ────────────────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [totalPages, setTotalPages] = useState(null);
  const [totalCount, setTotalCount] = useState(null);

  // ── Transaction drawer ────────────────────────────────────────────────────
  const [selectedPassenger, setSelectedPassenger] = useState(null);
  const [drawerBalance, setDrawerBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loadingTxns, setLoadingTxns] = useState(false);

  // Stale / duplicate response-a ignore panna request counters
  const listReqRef = useRef(0);
  const txnReqRef = useRef(0);

  const isSearchMode = activeTerm !== "";

  const displayBalance = (p) =>
    balanceOverrides[p.passengerId] !== undefined
      ? balanceOverrides[p.passengerId]
      : getListBalance(p);

  // ── ONE API call → passengers + wallet balance (10 per page) ─────────────
  const fetchPassengers = useCallback(async (pageNo, term) => {
    const reqId = ++listReqRef.current;
    setLoadingList(true);

    const data = await postPassengersWalletByAdmin({
      n_limit: PAGE_SIZE,
      n_page: pageNo,
      c_search_term: term,
    });

    if (reqId !== listReqRef.current) return undefined; // pazhaya response, ignore

    if (data.success) {
      const list = data.passengers || [];
      setPassengers(list);
      setBalanceOverrides({});
      setPage(pageNo);

      // Backend total / totalPages anuppina use pannum, illana full page vandha next irukku nu eduthukkum
      const total =
        data.total ?? data.totalCount ?? data.pagination?.total ?? null;
      const pages =
        data.totalPages ??
        data.pagination?.totalPages ??
        (total !== null ? Math.ceil(total / PAGE_SIZE) : null);
      setTotalCount(total);
      setTotalPages(pages);
      setHasNext(pages !== null ? pageNo < pages : list.length === PAGE_SIZE);

      if (list.length && list.every((p) => getListBalance(p) === null)) {
        console.warn(
          "[PassengerWallet] Passenger list response-la walletBalance illa. Backend post-all-passengers-la walletBalance add pannanum.",
        );
      }
    } else {
      toast.error(data.error || "Could not load passengers");
    }

    setLoadingList(false);
    return data;
  }, []);

  // ── First load: latest 10 ────────────────────────────────────────────────
  useEffect(() => {
    fetchPassengers(1, "");
  }, [fetchPassengers]);

  const closeDrawer = () => {
    txnReqRef.current++;
    setSelectedPassenger(null);
    setLoadingTxns(false);
  };

  // ── Search ────────────────────────────────────────────────────────────────
  const handleSearch = async (e) => {
    e.preventDefault();
    if (loadingList) return;
    const term = searchTerm.trim();
    closeDrawer();
    setActiveTerm(term);
    const data = await fetchPassengers(1, term);
    if (term && data?.success && (data.passengers || []).length === 0) {
      toast.info("No passengers found for that search");
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setActiveTerm("");
    closeDrawer();
    fetchPassengers(1, "");
  };

  // ── Prev / Next ───────────────────────────────────────────────────────────
  const goToPage = (pageNo) => {
    if (loadingList || pageNo < 1) return;
    closeDrawer();
    fetchPassengers(pageNo, activeTerm);
  };

  // ── Transaction drawer: click panna mattum, oru thadava ──────────────────
  const handleViewTransactions = async (passenger) => {
    const reqId = ++txnReqRef.current;
    setSelectedPassenger(passenger);
    setDrawerBalance(displayBalance(passenger));
    setTransactions([]);
    setLoadingTxns(true);
    try {
      const data = await getPassengerWalletByAdmin(passenger.passengerId);
      if (reqId !== txnReqRef.current) return;
      if (data.success) {
        setTransactions(data.transactions || []);
        setDrawerBalance(data.balance);
        setBalanceOverrides((prev) => ({
          ...prev,
          [passenger.passengerId]: data.balance,
        }));
      } else {
        toast.error(data.error || "Could not load transaction history");
      }
    } finally {
      if (reqId === txnReqRef.current) setLoadingTxns(false);
    }
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div style={styles.page}>
      <h1 style={styles.pageTitle}>Passenger Wallets</h1>
      <p style={styles.pageSubtitle}>
        {isSearchMode
          ? `Showing search results for "${activeTerm}"`
          : "Latest passengers and their current wallet balance"}
      </p>

      {/* ── Search Bar ── */}
      <form onSubmit={handleSearch} style={styles.searchForm}>
        <input
          type="text"
          placeholder="Search by name, email, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={styles.searchInput}
        />
        <button type="submit" style={styles.searchBtn} disabled={loadingList}>
          {loadingList && isSearchMode ? "Searching..." : "Search"}
        </button>
        {isSearchMode && (
          <button
            type="button"
            style={styles.clearBtn}
            onClick={handleClearSearch}
          >
            Clear
          </button>
        )}
      </form>

      {/* ── Passenger + Wallet Table ── */}
      <div style={styles.tableCard}>
        {loadingList ? (
          <div style={styles.emptyState}>
            <p style={styles.emptyText}>Loading passengers...</p>
          </div>
        ) : passengers.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>👤</div>
            <p style={styles.emptyText}>No passengers found</p>
            {page > 1 && (
              <button
                style={{ ...styles.pageBtn, ...styles.pageBtnGhost }}
                onClick={() => goToPage(page - 1)}
              >
                ← Previous
              </button>
            )}
          </div>
        ) : (
          <>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Passenger</th>
                    <th style={styles.th}>Phone</th>
                    <th style={styles.th}>Email</th>
                    <th style={{ ...styles.th, textAlign: "right" }}>
                      Wallet Balance
                    </th>
                    <th style={{ ...styles.th, textAlign: "center" }}>
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {passengers.map((p) => {
                    const balance = displayBalance(p);
                    return (
                      <tr key={p.passengerId}>
                        <td style={styles.td}>
                          <div style={styles.nameCell}>
                            <div style={styles.avatar}>
                              {(p.name || "?").charAt(0).toUpperCase()}
                            </div>
                            <span>{p.name || "Unnamed"}</span>
                          </div>
                        </td>
                        <td style={styles.td}>{p.phone || "—"}</td>
                        <td style={styles.td}>{p.email || "—"}</td>
                        <td
                          style={{
                            ...styles.td,
                            textAlign: "right",
                            fontWeight: 700,
                            color: BRAND.primaryDark,
                          }}
                        >
                          {balance === null ? (
                            <span style={{ color: "#9CA3AF", fontWeight: 400 }}>
                              N/A
                            </span>
                          ) : (
                            `₹${balance.toFixed(2)}`
                          )}
                        </td>
                        <td style={{ ...styles.td, textAlign: "center" }}>
                          <button
                            style={styles.viewBtn}
                            onClick={() => handleViewTransactions(p)}
                          >
                            View Transactions
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* ── Pagination ── */}
            <div style={styles.pagination}>
              <span style={styles.pageInfo}>
                Page {page}
                {totalPages ? ` of ${totalPages}` : ""} · Showing{" "}
                {passengers.length}
                {totalCount !== null ? ` of ${totalCount}` : ""} records
              </span>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  style={{
                    ...styles.pageBtn,
                    ...styles.pageBtnGhost,
                    ...(page <= 1 ? styles.pageBtnDisabled : {}),
                  }}
                  disabled={page <= 1}
                  onClick={() => goToPage(page - 1)}
                >
                  ← Previous
                </button>
                <button
                  style={{
                    ...styles.pageBtn,
                    ...(!hasNext ? styles.pageBtnDisabled : {}),
                  }}
                  disabled={!hasNext}
                  onClick={() => goToPage(page + 1)}
                >
                  Next →
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Transaction Drawer ── */}
      {selectedPassenger && (
        <div style={styles.drawerOverlay} onClick={closeDrawer}>
          <div style={styles.drawer} onClick={(e) => e.stopPropagation()}>
            <div style={styles.drawerHeader}>
              <div>
                <div style={styles.walletPassengerName}>
                  {selectedPassenger.name}
                </div>
                <div style={styles.walletPassengerMeta}>
                  {selectedPassenger.phone} · {selectedPassenger.email}
                </div>
              </div>
              <button style={styles.closeBtn} onClick={closeDrawer}>
                ✕
              </button>
            </div>

            <div style={styles.balanceBadge}>
              <div style={styles.balanceBadgeLabel}>Wallet Balance</div>
              <div style={styles.balanceBadgeValue}>
                ₹{(drawerBalance ?? 0).toFixed(2)}
              </div>
            </div>

            <h3 style={styles.panelTitle}>Transaction History</h3>

            {loadingTxns ? (
              <p style={styles.emptyText}>Loading transactions...</p>
            ) : transactions.length === 0 ? (
              <p style={styles.emptyText}>No transactions yet</p>
            ) : (
              <div style={styles.txnList}>
                {transactions.map((txn) => (
                  <div key={txn._id} style={styles.txnRow}>
                    <div
                      style={{
                        ...styles.typeBadge,
                        ...(txn.type === "credit"
                          ? styles.creditBadge
                          : styles.debitBadge),
                      }}
                    >
                      {txn.type === "credit" ? "Credit" : "Debit"}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={styles.txnDesc}>{txn.description}</div>
                      <div style={styles.txnDate}>
                        {formatDate(txn.createdAt)} · {txn.source}
                      </div>
                    </div>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 14,
                        color: txn.type === "credit" ? "#16A34A" : "#DC2626",
                      }}
                    >
                      {txn.type === "credit" ? "+" : "-"}₹
                      {txn.amount.toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// ✅ INLINE STYLES
// ════════════════════════════════════════════════════════════════════════════
const styles = {
  page: { padding: "28px 32px", fontFamily: "inherit" },
  pageTitle: { fontSize: 24, fontWeight: 700, color: "#111827", margin: 0 },
  pageSubtitle: { fontSize: 14, color: "#6B7280", marginTop: 4 },

  searchForm: { display: "flex", gap: 10, marginTop: 20, maxWidth: 560 },
  searchInput: {
    flex: 1,
    padding: "11px 14px",
    borderRadius: 10,
    border: "1px solid #E5E7EB",
    fontSize: 14,
    outline: "none",
  },
  searchBtn: {
    padding: "11px 22px",
    borderRadius: 10,
    border: "none",
    backgroundColor: BRAND.primary,
    color: "#fff",
    fontWeight: 600,
    fontSize: 14,
    cursor: "pointer",
  },
  clearBtn: {
    padding: "11px 18px",
    borderRadius: 10,
    border: "1px solid #E5E7EB",
    backgroundColor: "#fff",
    color: "#374151",
    fontWeight: 600,
    fontSize: 14,
    cursor: "pointer",
  },

  // ── Table Card ──
  tableCard: {
    backgroundColor: "#fff",
    borderRadius: 14,
    border: "1px solid #F3F4F6",
    marginTop: 24,
    overflow: "hidden",
  },
  emptyState: {
    height: 220,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
  },
  emptyIcon: { fontSize: 36 },
  emptyText: { fontSize: 13, color: "#9CA3AF" },

  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left",
    fontSize: 12,
    fontWeight: 700,
    color: "#6B7280",
    padding: "12px 16px",
    borderBottom: "1px solid #E5E7EB",
    whiteSpace: "nowrap",
    backgroundColor: "#FAFAFA",
  },
  td: {
    fontSize: 13,
    color: "#374151",
    padding: "12px 16px",
    borderBottom: "1px solid #F3F4F6",
  },
  nameCell: { display: "flex", alignItems: "center", gap: 10 },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: BRAND.primary,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 13,
    flexShrink: 0,
  },
  loadingDots: { color: "#D1D5DB" },
  viewBtn: {
    padding: "7px 14px",
    borderRadius: 8,
    border: `1px solid ${BRAND.primaryBorder}`,
    backgroundColor: BRAND.primaryBg,
    color: BRAND.primaryDark,
    fontWeight: 600,
    fontSize: 12,
    cursor: "pointer",
  },

  // ── Transaction Drawer ──
  drawerOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.35)",
    display: "flex",
    justifyContent: "flex-end",
    zIndex: 1000,
  },
  drawer: {
    width: 420,
    maxWidth: "90vw",
    height: "100%",
    backgroundColor: "#fff",
    padding: 24,
    overflowY: "auto",
    boxShadow: "-4px 0 20px rgba(0,0,0,0.08)",
  },
  drawerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 16,
  },
  closeBtn: {
    border: "none",
    background: "#F3F4F6",
    borderRadius: 8,
    width: 30,
    height: 30,
    cursor: "pointer",
    fontSize: 14,
    color: "#6B7280",
  },
  walletPassengerName: { fontSize: 17, fontWeight: 700, color: "#111827" },
  walletPassengerMeta: { fontSize: 12, color: "#6B7280", marginTop: 4 },

  balanceBadge: {
    backgroundColor: BRAND.primaryBg,
    border: `1px solid ${BRAND.primaryBorder}`,
    borderRadius: 12,
    padding: "14px 18px",
    marginBottom: 20,
  },
  balanceBadgeLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: BRAND.primaryDark,
  },
  balanceBadgeValue: {
    fontSize: 24,
    fontWeight: 800,
    color: BRAND.primaryDark,
    marginTop: 2,
  },

  panelTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: "#374151",
    marginBottom: 12,
  },
  txnList: { display: "flex", flexDirection: "column", gap: 10 },
  txnRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 0",
    borderBottom: "1px solid #F3F4F6",
  },
  txnDesc: { fontSize: 13, fontWeight: 600, color: "#1F2937" },
  txnDate: { fontSize: 11, color: "#9CA3AF", marginTop: 2 },
  typeBadge: {
    fontSize: 10,
    fontWeight: 700,
    padding: "3px 8px",
    borderRadius: 20,
    whiteSpace: "nowrap",
  },
  creditBadge: { backgroundColor: "#ECFDF5", color: "#16A34A" },
  debitBadge: { backgroundColor: "#FEF2F2", color: "#DC2626" },

  // ── Pagination ──
  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    padding: "14px 16px",
    borderTop: "1px solid #F3F4F6",
    backgroundColor: "#FAFAFA",
  },
  pageInfo: { fontSize: 13, color: "#6B7280" },
  pageBtn: {
    padding: "8px 16px",
    borderRadius: 8,
    border: `1px solid ${BRAND.primaryBorder}`,
    backgroundColor: BRAND.primary,
    color: "#fff",
    fontWeight: 600,
    fontSize: 13,
    cursor: "pointer",
  },
  pageBtnGhost: {
    backgroundColor: "#fff",
    color: BRAND.primaryDark,
  },
  pageBtnDisabled: {
    opacity: 0.45,
    cursor: "not-allowed",
  },
};
